<script setup>
  import {computed, inject, onMounted, ref, watch} from "vue";

  import ComponentIcon from "@components/Icons/Icon.vue";
  import {Deferred as InertiaDeferred, router, usePage} from "@inertiajs/vue3";
  import MedicationTableRow from "@pages/Records/Components/Sections/MedicationBar/Components/Row.vue";
  import MedicationSkeletonRow from "@pages/Records/Components/Sections/MedicationBar/Components/SkeletonRow.vue";
  import {MedButton, MedCheckbox, MedInfoButton} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {sortList, sortListByIndicationAndFam, sortListByUsageAndFam} from "@utils/Helpers/ListSorter.js";
  import {cloneDeep, debounce, isEqual} from "es-toolkit";
  import Sortable from "sortablejs";

  defineOptions({
    name: "MedicationTable",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      required: true,
    },
    recordMedications: {
      type: Array,
      default: () => [],
    },
    mode: {
      type: String,
      default: null,
    },
  });

  defineEmits(["updateMedications", "cancelUpdateMedications"]);

  const page = usePage();

  const setIsMassUpdateProcessing = inject("setIsMassUpdateProcessing");
  const deleteDialog = inject("recordEditDeleteRowDialog");

  const refDraggable = ref(null);

  const isDragging = ref(false);
  const medications = ref([...props.recordMedications]);
  const pendingSortOrder = ref(null);
  const sortable = ref(null);
  const sortCancelToken = ref(null);
  const selectedRows = ref([]);

  const getMedicationIds = (items) => items.map((medication) => medication.id);

  onMounted(() => {
    initSortable();
  });

  const sortDirection = computed(() => {
    if (medications.value?.length > 0) {
      const copyOfMedications = cloneDeep(medications.value);
      const medicationIds = copyOfMedications.map((medication) => medication.id);

      const indicationAscSortedObject = sortListByIndicationAndFam(copyOfMedications, "asc");

      if (isEqual(medicationIds, indicationAscSortedObject.sortedIds)) {
        return "indication.asc";
      }

      if (isEqual(medicationIds, indicationAscSortedObject.sortedIds.toReversed())) {
        return "indication.desc";
      }

      const usageAscSortedObject = sortListByUsageAndFam(copyOfMedications, "asc");

      if (isEqual(medicationIds, usageAscSortedObject.sortedIds)) {
        return "usage.asc";
      }

      if (isEqual(medicationIds, usageAscSortedObject.sortedIds.toReversed())) {
        return "usage.desc";
      }

      const famAscSortedObject = sortList(copyOfMedications, "fam", "asc");

      if (isEqual(medicationIds, famAscSortedObject.sortedIds)) {
        return "fam.asc";
      }

      if (isEqual(medicationIds, famAscSortedObject.sortedIds.toReversed())) {
        return "fam.desc";
      }
    }

    return "";
  });

  const newSortDirection = computed(() => {
    const splitValue = sortDirection.value.includes(".") ? sortDirection.value.split(".") : sortDirection.value;

    if (splitValue === "" || splitValue[1] === "desc") {
      return "asc";
    }

    if (splitValue[1] === "asc") {
      return "desc";
    }

    return "";
  });

  watch(
    () => props.recordMedications,
    (newMedications) => {
      const incomingSortOrder = getMedicationIds(newMedications);

      if (
        pendingSortOrder.value !== null &&
        newMedications.length === medications.value.length &&
        !isEqual(incomingSortOrder, pendingSortOrder.value)
      ) {
        return;
      }

      medications.value = [...newMedications];

      if (pendingSortOrder.value !== null && isEqual(incomingSortOrder, pendingSortOrder.value)) {
        pendingSortOrder.value = null;
      }
    },
  );

  watch(
    () => medications.value,
    (value, oldValue) => {
      if (value.length !== oldValue?.length) {
        initSortable();
      }

      // remove deleted ids from selectedRows
      const newIds = new Set(value.map((medication) => medication.id));
      selectedRows.value = selectedRows.value.filter((id) => newIds.has(id));
    },
  );

  const initSortable = () => {
    if (sortable.value !== null) {
      return; // already initialized
    }
    if (refDraggable.value === null) {
      return; // refDraggable is not available yet
    }

    sortable.value = Sortable.create(refDraggable.value, {
      handle: "[drag-handle]",
      draggable: "[drag-item]",
      direction: "vertical",
      ghostClass: "sortable-ghost",
      dataIdAttr: "drag-item", // requires `drag-item="{{item.id}}"`
      store: {
        /**
         * Save the order of elements. Called onEnd (when the item is dropped).
         * @param {Sortable} sortable
         */
        set: (sortable) => {
          // sort medications according to new sort order
          updateOrder(sortable.toArray());
        },
      },
      onStart: () => {
        isDragging.value = true;
      },
      onEnd: () => {
        isDragging.value = false;
      },
    });
  };

  const updateOrder = (newSortOrder) => {
    const currentSortOrder = getMedicationIds(medications.value);

    if (isEqual(currentSortOrder, newSortOrder)) {
      return;
    }

    pendingSortOrder.value = [...newSortOrder];

    medications.value = medications.value.toSorted((a, b) => {
      return newSortOrder.indexOf(a.id) - newSortOrder.indexOf(b.id);
    });

    if (sortCancelToken.value) {
      sortCancelToken.value.cancel();
    }

    // lock immediately (not inside the debounced request) so adding/removing a
    // medication can't race with the pending sort request and desync the ids snapshot
    setIsMassUpdateProcessing("medication", true);

    updateOrderRequest(newSortOrder);
  };

  const updateOrderRequest = debounce((newSortOrder) => {
    router.post(
      route("sort.store", {
        patient: page.props.patient.id,
        record: page.props.record.id,
        relation: "medications",
      }),
      {
        ids: newSortOrder,
      },
      {
        preserveScroll: true,
        only: ["flash", "medications"],
        onFinish: () => {
          setIsMassUpdateProcessing("medication", false);
        },
        onCancelToken: (cancelToken) => (sortCancelToken.value = cancelToken),
      },
    );
  }, 2000);

  const sortByFam = () => {
    if (!props.isEditable) return;

    const sortedObject = sortList(medications.value, "fam", newSortDirection.value);

    updateOrder(sortedObject.sortedIds);
  };

  const sortByUsageAndFam = () => {
    if (!props.isEditable) return;

    const sortedObject = sortListByUsageAndFam(medications.value, newSortDirection.value);

    updateOrder(sortedObject.sortedIds);
  };

  const sortByIndication = () => {
    if (!props) return;

    const sortedObject = sortListByIndicationAndFam(medications.value, newSortDirection.value);

    updateOrder(sortedObject.sortedIds);
  };

  const showDeleteConfirmDialog = () => {
    deleteDialog({routeName: "medications", payload: {medication: selectedRows.value}});
  };

  const selectAllState = computed({
    get: () => {
      if (selectedRows.value.length === medications.value.length) return true;
      if (selectedRows.value.length > 0) return "indeterminate";
      return false;
    },
    set: (value) => {
      selectedRows.value = value ? medications.value.map((m) => m.id) : [];
    },
  });

  const toggleRow = (id) => {
    const index = selectedRows.value.indexOf(id);
    if (index !== -1) {
      selectedRows.value.splice(index, 1);
    } else {
      selectedRows.value.push(id);
    }
  };
</script>

<template>
  <InertiaDeferred data="medications">
    <template #fallback />

    <template v-if="medications.length > 0">
      <!-- ☑️ multiselect actions -->
      <section v-if="mode === 'multi-select' && selectedRows?.length" class="items-top mb-2 flex space-x-1">
        <div class="my-2 rounded border-2 border-transparent bg-gray-200 px-1 py-0 text-xs font-semibold text-gray-700">
          {{ selectedRows?.length }}
        </div>
        <div class="flex flex-wrap gap-0.5">
          <MedButton
            flat
            icon="delete"
            :data-test="getTestId('default-head-bulk-delete-button')"
            @click="showDeleteConfirmDialog()"
          >
            Löschen
          </MedButton>
        </div>
      </section>

      <div
        class="grid grid-cols-[1fr_1fr_140px_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr] items-end gap-x-2 border-b-1 border-gray-300 pb-2 text-sm font-normal text-gray-700"
      >
        <div class="col-span-2 flex items-center">
          <template v-if="isEditable">
            <div class="flex size-6 shrink-0 items-center justify-center stroke-0">
              <MedCheckbox
                v-if="mode === 'multi-select'"
                v-model="selectAllState"
                aria-label="Alle Zeilen auswählen"
                variant="primary"
                :data-test="getTestId('table-select-all-checkbox')"
              />
            </div>

            <div :class="cn('ml-2')">
              <div>
                <button class="flex items-center text-gray-700 hover:no-underline" @click.prevent="sortByFam">
                  <template v-if="sortDirection === 'fam.asc'">
                    <ComponentIcon>expand_more</ComponentIcon>
                  </template>

                  <template v-else-if="sortDirection === 'fam.desc'">
                    <ComponentIcon>expand_less</ComponentIcon>
                  </template>

                  <template v-else>
                    <ComponentIcon>unfold_more</ComponentIcon>
                  </template>

                  <span>Arzneimittel</span>
                </button>
              </div>

              <div class="flex space-x-1">
                <button class="flex items-center text-gray-700 hover:no-underline" @click.prevent="sortByUsageAndFam">
                  <template v-if="sortDirection === 'usage.asc'">
                    <ComponentIcon>expand_more</ComponentIcon>
                  </template>

                  <template v-else-if="sortDirection === 'usage.desc'">
                    <ComponentIcon>expand_less</ComponentIcon>
                  </template>

                  <template v-else>
                    <ComponentIcon>unfold_more</ComponentIcon>
                  </template>

                  <span class="text-xs">Art</span>
                </button>

                <MedInfoButton
                  size="lg"
                  side="top"
                  aria-label="Begriffserklärungen Medikationsart"
                  :data-test="getTestId('medication-type-info-button')"
                >
                  <p class="mb-2 text-center font-bold">Begriffserklärungen Medikationsart</p>
                  <dl class="space-y-2 text-sm">
                    <div>
                      <dt class="font-bold">Dauer</dt>
                      <dd>
                        Anwendung eines oder mehrerer Medikamente über einen längeren Zeitraum (meist > 3 Monate).
                      </dd>
                    </div>
                    <div>
                      <dt class="font-bold">Kurzzeit</dt>
                      <dd>Anwendung über ein kurzes, begrenztes Zeitintervall.</dd>
                    </div>
                    <div>
                      <dt class="font-bold">Bedarf</dt>
                      <dd>
                        Anwendung bei entsprechenden Symptomen (z.B. Schmerzen, Übelkeit) zusätzlich zur regulären
                        Medikation.
                      </dd>
                    </div>
                  </dl>
                </MedInfoButton>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="ml-2">Arzneimittel</div>
            <div class="ml-2 text-xs">Art</div>
          </template>
        </div>

        <div class="col-span-1 flex items-center space-x-1">
          <span class="text-xs">Dosierung</span>
          <MedInfoButton
            size="lg"
            side="top"
            aria-label="Dosierungsangaben"
            :data-test="getTestId('dosage-info-button')"
          >
            <p class="mb-2 text-center font-bold">Dosierungsangaben</p>
            <p class="max-w-md text-sm">
              <strong>Achtung:</strong>
              Automatisiert überprüft wurden nur die Tagesdosen fester Arzneiformen (TAB, FTA, BTA, KAP, RET, LUT,
              Pulver, Granulate, SUPP) gegen die Regeldosis-Spanne für Erwachsene, Ältere und Niereninsuffiziente. Keine
              indikationsabhängige Prüfung. Bei Doppelverordnungen eingeschränkt. Details bitte der Fachinformation
              entnehmen.
            </p>
          </MedInfoButton>
        </div>

        <div class="col-span-2 flex min-h-7 items-center text-xs">Einheit</div>

        <div class="col-span-2 flex items-center space-x-1">
          <span class="text-xs">Mahlzeit</span>
          <MedInfoButton
            size="lg"
            side="top"
            aria-label="Begriffserklärungen Mahlzeit"
            :data-test="getTestId('meal-info-button')"
          >
            <p class="mb-2 text-center font-bold">Begriffserklärungen Mahlzeit</p>
            <dl class="space-y-2 text-sm">
              <div>
                <dt class="font-bold">Vor den Mahlzeiten</dt>
                <dd>Kontakt mit Nahrung im GIT vermeiden, z.B. 30–60 Min. vor der Mahlzeit.</dd>
              </div>
              <div>
                <dt class="font-bold">Zwischen den Mahlzeiten</dt>
                <dd>Kontakt mit Nahrung vermeiden, z.B. mind. 2 Std. nach einer Mahlzeit.</dd>
              </div>
              <div>
                <dt class="font-bold">Zu oder nach den Mahlzeiten</dt>
                <dd>Zeitnah zur Mahlzeit oder innerhalb von 15 Min. nach dem Essen.</dd>
              </div>
              <div>
                <dt class="font-bold">Unabhängig von Mahlzeiten</dt>
                <dd>Nahrung hat keinen relevanten Einfluss → Patient kann frei wählen.</dd>
              </div>
            </dl>
          </MedInfoButton>
        </div>

        <div class="col-span-4 flex min-h-7 items-center text-xs">
          <template v-if="isEditable">
            <button class="flex items-center text-gray-700 hover:no-underline" @click.prevent="sortByIndication()">
              <template v-if="sortDirection === 'indication.asc'">
                <ComponentIcon>expand_more</ComponentIcon>
              </template>

              <template v-else-if="sortDirection === 'indication.desc'">
                <ComponentIcon>expand_less</ComponentIcon>
              </template>

              <template v-else>
                <ComponentIcon>unfold_more</ComponentIcon>
              </template>
              <span>Grund/Ind.</span>
            </button>
          </template>
          <template v-else>
            <span>Grund/Ind.</span>
          </template>
        </div>

        <div class="col-span-1 inline-flex min-h-7 items-center justify-center text-xs">Details</div>
      </div>
    </template>
  </InertiaDeferred>

  <ul ref="refDraggable" :data-test="getTestId('table')">
    <InertiaDeferred data="medications">
      <template #fallback>
        <template v-for="index in 5" :key="index">
          <MedicationSkeletonRow :test-id="getTestId(`table-${index}`)" />
        </template>
      </template>

      <template v-for="(medication, index) in medications" :key="medication.id">
        <MedicationTableRow
          :drag-item="medication.id"
          :is-editable="isEditable"
          :medication="medication"
          :is-dragging="isDragging"
          :test-id="getTestId(`table-${index}`)"
          @update-medications="$emit('updateMedications')"
          @cancel-update-medications="$emit('cancelUpdateMedications')"
        >
          <template v-if="mode === 'multi-select'" #prefix>
            <div class="flex size-6 shrink-0 items-center justify-center stroke-0">
              <MedCheckbox
                :model-value="selectedRows.includes(medication.id)"
                :aria-label="`Zeile ${medication.fam} auswählen`"
                variant="primary"
                :data-test="getTestId('table-row-select-checkbox')"
                @update:model-value="toggleRow(medication.id)"
              />
            </div>
          </template>
        </MedicationTableRow>
      </template>
    </InertiaDeferred>
  </ul>
</template>
